import {useEffect, useState, useRef} from 'react';
import {Link, useSearchParams} from 'react-router';
import {Card, Checkbox, Table, Popover, Radio, Row, Col, message} from 'antd';
import {getBaseColumnSearchProps, getColumnSearchProps} from "../widget/SearchProps";
import {
    getAssignedTasks,
    getOwnedTasks,
    updateTaskAssignedTo,
    getAllTasks,
    getTasksByDepartment,
    updateTaskDueDate, getFollowedTasks
} from "../../api/Api";

import {TaskStatusWidget} from "./TaskStatusWidget";
import {UserSelectWidget} from "./UserSelectWidget";
import {TaskDueWidget} from "./TaskDueWidget";
import {TaskStatusType, TaskDueType} from "../../api/Constants";
import {TaskDueIndicator} from "./TaskDueIndicator";
import {checkPerm} from "../../auth/sessionTools";
import {toFSDateTime, toFSDate, toMs} from "../../util/timeutils";
import {getStatusColumnSearchProps} from "./TaskStatusSearch";

export const TaskTable = ({refresh, onRefresh, onApiFail, session, department, onSearchParamsChange, showVerified=false, taskType="owned"}) => {
    // searchParams is the source of truth for column sorting and searching
    const [searchParams] = useSearchParams();
    const dueFilter = parseInt(searchParams.get("due"), 10);
    const initialDueFilter = ((dueFilter === null) || (dueFilter ===undefined)) ? TaskDueType.None : dueFilter;

    const [tasks, setTasks] = useState([]);
    const [assignMap, setAssignMap] = useState({});
    const [dueCount, setDueCount] = useState({});
    const [verified, setVerified] = useState(false);
    const [filterOption, setFilterOption] = useState(initialDueFilter);
    const [messageApi, contextHolder] = message.useMessage();
    const isOperations = checkPerm(session, "operations");

    const titleSearch = searchParams.get('title');
    const titleSearchVal = titleSearch ? [titleSearch] : [];
    const assigneeSearch = searchParams.get('assignee');
    const assigneeSearchVal = assigneeSearch ? [assigneeSearch] : [];
    const departmentSearch = searchParams.get('department');
    const departmentSearchVal = departmentSearch ? [departmentSearch] : [];
    const statusSearch = searchParams.get('status');
    const statusSearchVal = statusSearch ? [statusSearch] : [];

    useEffect(() => {
        let taskPromise;
        if (taskType === "owned") {
            taskPromise = getOwnedTasks();
        } else if (taskType === "assigned") {
            taskPromise = getAssignedTasks();
        } else if (taskType === "all") {
            taskPromise = getAllTasks();
        } else if (taskType === "department") {
            taskPromise = getTasksByDepartment(department);
        } else if (taskType === "followed") {
            taskPromise = getFollowedTasks();
        }
        taskPromise.then(r => {
            const result = r.map(line => {
                return {
                    ...line,
                    key: line.id,
                    // patch any null departments
                    department: line.department || "Not Assigned",
                }
            });

            setTasks(result);

            // go through the tasks and set the assignees
            const newMap = {};
            // count by due date
            let dueNow = 0, dueSoon = 0, overDue = 0;
            const now = new Date();
            const nowString = toFSDate(now);

            result.forEach(task => {
                if ((task.assignedTo !== undefined) && (task.assignedTo !== null)) {
                    const {assignee} = task;
                    newMap[task.id] = assignee;
                }

                if (task.status === TaskStatusType.Verified) {
                    // don't count any verified task
                    return;
                }
                const dueDate = new Date(task.dueDate);
                const dueDateString = toFSDate(dueDate);
                if ((dueDate - now) <= toMs("2 days")) {
                    dueSoon ++;
                }
                if (dueDateString === nowString) {
                    dueNow ++;
                }
                if ((now - dueDate) >= toMs("1 day")) {
                    overDue ++;
                }
            });
            setAssignMap(newMap);
            setDueCount({dueNow, dueSoon, overDue});

        }).catch (e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }, [refresh, onApiFail, taskType, department]);

    useEffect(() => {

    }, [])

    const setAssignment = (task) => (user) => {
        // console.log(`set assignment ${task.id} ${JSON.stringify(user)}`)
        // console.log(`${JSON.stringify(assignMap[task.id])}`);
        if ((assignMap[task.id] === undefined) || (assignMap[task.id].id !== user.id)) {
            // set the map
            const newMap = {...assignMap};
            newMap[task.id] = user;
            setAssignMap(newMap);

            updateTaskAssignedTo(task.id, user.id).then(r => {
                // console.log(`task assignTo updated to ${user.id}`);
                if (onRefresh) onRefresh();
            }).catch(e => {
                if ((e.status === 401) && (onApiFail)) {
                    onApiFail();
                }
            })
        }
    }

    const setDueDate = (task) => (dateString) => {
        updateTaskDueDate(task.id, dateString).then(r => {
            messageApi.open({
                type: "success",
                content: "Due Date Updated"
            });
            if (onRefresh) onRefresh();
        }).catch (e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
            messageApi.open({
                type: "error",
                content: "Failed to Update Due Date"
            })
        })
    }

    const onFilterChange = (e) => {
        setFilterOption(e.target.value);
        if (onSearchParamsChange) {
            const newParams = {};
            newParams["due"] = e.target.value;
            onSearchParamsChange(newParams)
        }
    }

    const setColumnSearch = (column, searchTerm) => {
        if (onSearchParamsChange) {
            const newParams = {};
            newParams[column] = searchTerm;
            onSearchParamsChange(newParams)
        }
    }

    // extract the sort column, and sort order
    const sortColumn = searchParams.get("sortColumn");
    const sortOrder = searchParams.get("sortOrder");
    // helper for finding sort order
    const getSortOrder = (column) => {
        if (sortColumn === column) {
            if (sortOrder) {
                // console.log(`getSortOrder ${sortColumn} ${sortOrder}`)
                return sortOrder;
            }
        }
    }

    const searchInput = useRef(null);

    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (val, record, index) => {
                return <Popover content={'View Task'}>
                    <Link to={`/task/${encodeURIComponent(record.id)}`}>{val}</Link>
                </Popover>
            },
            sorter: (a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()),
            ...getColumnSearchProps('title', searchInput, setColumnSearch),
            filteredValue: titleSearchVal,
            sortOrder: getSortOrder("title"),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (val, record, index) => {
                const {createdBy} = record;
                const allowVerified = (session && (session.id === createdBy)) || isOperations;
                return <TaskStatusWidget task={record} onRefresh={onRefresh} onApiFail={onApiFail} allowVerified={allowVerified} />
            },
            sorter: (a, b) => (a.status - b.status),
            sortOrder: getSortOrder("status"),
            ...getStatusColumnSearchProps('status', searchInput, setColumnSearch),
            filteredValue: statusSearchVal,
        },
        {
            title: 'Created',
            dataIndex: 'timestamp',
            key: 'timestamp',
            sorter: (a, b) => (new Date(a.timestamp))- (new Date(b.timestamp)),
            render: (val, record) => {
                return toFSDateTime(new Date(val));
            },
            sortOrder: getSortOrder("timestamp"),
        },
        {
            title: 'Due By',
            dataIndex: 'dueDate',
            key: 'dueDate',
            sorter: (a, b) => (new Date(a.dueDate))- (new Date(b.dueDate)),
            render: (val, record) => {
                const disabled = record.status === TaskStatusType.Verified;
                let dueDateChange = !disabled && ((taskType === "owned") || (isOperations)) ? setDueDate(record) : undefined;
                return <TaskDueWidget due={val} onChange={dueDateChange} />
            },
            sortOrder: getSortOrder("dueDate"),
        },
        {
            title: 'Assigned To',
            dataIndex: 'assignedTo',
            key: 'assignedTo',
            render: (val, record, index) => {
                const disabled = record.status === TaskStatusType.Verified;
                return <UserSelectWidget user={assignMap[record.id]} altText={`assign to`} onApiFail={onApiFail} setUser={setAssignment(record)} disabled={disabled} />
            },
            sorter: (a, b) => {
                let aName = "zzzzzzzzzzzzzzzzzzzzzzzzzzz", bName = "zzzzzzzzzzzzzzzzzzzzzzzzzzz";
                if (a.assignee) {
                    aName = `${a.assignee.firstname} ${a.assignee.lastname}`
                }
                if (b.assignee) {
                    bName = `${b.assignee.firstname} ${b.assignee.lastname}`
                }

                return aName.toLowerCase().localeCompare(bName.toLowerCase());
            },
            ...getBaseColumnSearchProps('assignee', searchInput, setColumnSearch),
            onFilter: (value, record) => {
                if ((record.assignee === null) || (record.assignee === undefined)) {
                    return false;
                }
                const name = `${record.assignee.firstname} ${record.assignee.lastname}`;
                return name.toLowerCase().includes(value.toLowerCase());
            },
            filteredValue: assigneeSearchVal,
            sortOrder: getSortOrder("assignedTo"),
        },
    ];

    if (taskType !== "owned") {
        columns.push({
            title: 'Created By',
            dataIndex: 'creator',
            key: 'creator',
            render: (val, record, index) => {
                return `${record.creator.firstname} ${record.creator.lastname}`;
            },
            sorter: (a, b) => {
                const aName = `${a.creator.firstname} ${a.creator.lastname}`
                const bName = `${b.creator.firstname} ${b.creator.lastname}`
                return aName.toLowerCase().localeCompare(bName.toLowerCase());
            },
            sortOrder: getSortOrder("creator"),
        })
    }

    if (taskType !== "department") {
        columns.push(
            {
                title: 'Department',
                dataIndex: 'department',
                key: 'department',
                sorter: (a, b) => {
                    const aDept = a.department;
                    const bDept = b.department;
                    return aDept.toLowerCase().localeCompare(bDept.toLowerCase())
                },
                ...getColumnSearchProps('department', searchInput, setColumnSearch),
                filteredValue: departmentSearchVal,
                sortOrder: getSortOrder("department"),
            }
        );
    }

    const tablePagination = {
        defaultPageSize: 50,
        showSizeChanger: true,
        pageSizeOptions: [50, 100, 150, 200]
    }

    const onShowVerified = (e) => {
        setVerified(e.target.checked);
    }

    const onTableChange = (pagination, filters, sorter, extra) => {
        // Check if the change was triggered by sorting
        if (extra.action === 'sort') {
            // console.log('Sorted Column:', sorter.columnKey);
            // console.log('Sort Order:', sorter.order);
            if (onSearchParamsChange) {
                if (sorter.columnKey) {
                    const newParams = {
                        sortColumn: sorter.columnKey,
                        sortOrder: sorter.order
                    }
                    onSearchParamsChange(newParams);
                } else {
                    const newParams = {
                        sortColumn: null,
                        sortOrder: null
                    }
                    onSearchParamsChange(newParams);
                }
            }
        }
    }

    // filter if we are not displaying verified tasks
    let filteredTasks = tasks;
    if (verified !== true) {
        filteredTasks = tasks.filter(t => t.status !== TaskStatusType.Verified)
    }

    // filter for due date
    if (filterOption === TaskDueType.Today) {
        // due today
        filteredTasks = filteredTasks.filter(t => {
            const dueDate = toFSDate(new Date (t.dueDate));
            const now = toFSDate(new Date());
            return (dueDate === now);
        })
    } else if (filterOption === TaskDueType.Soon) {
        // due soon
        filteredTasks = filteredTasks.filter(t => {
            const dueDate = new Date(t.dueDate);
            const now = new Date();
            return (dueDate - now) <= toMs("2 days");
        })
    } else if (filterOption === TaskDueType.OverDue) {
        filteredTasks = filteredTasks.filter(t => {
            const dueDate = new Date (t.dueDate);
            const now = new Date();
            return ((now - dueDate) >= toMs("1 day"));
        })
    }

    return <div>
        {contextHolder}
        {((taskType !== "owned") || (showVerified === true)) &&
            <Card title={<b>Filter Options</b>} size={`small`} style={{textAlign: 'left', marginBottom: '5px'}}>
                {(showVerified === true) &&
                    <div>
                        <Row style={{margin: '5px', marginBottom: '10px'}}>
                            <Col style={{marginRight: '10px'}}>
                                Show Verified
                            </Col>
                            <Col>
                                <Checkbox onClick={onShowVerified}></Checkbox>
                            </Col>
                        </Row>
                    </div>
                }
                {(taskType !== "owned") &&
                <Radio.Group
                    value={filterOption}
                    onChange={onFilterChange}
                >
                    <div><Radio value={TaskDueType.None}>All Tasks</Radio></div>
                    <div>
                        <Row>
                            <Col>
                                <Radio value={TaskDueType.OverDue}>Over Due</Radio>
                            </Col>
                            <Col>
                                <TaskDueIndicator alert={TaskDueType.OverDue} />
                            </Col>
                            { (dueCount.overDue > 0) &&
                                <Col style={{marginLeft: '5px'}}>
                                    <b>{`${dueCount.overDue} tasks over due.`}</b>
                                </Col>
                            }
                        </Row>
                    </div>
                    <div>
                        <Row>
                            <Col>
                                <Radio value={TaskDueType.Today}>Due Today</Radio>
                            </Col>
                            <Col>
                                <TaskDueIndicator alert={TaskDueType.Today} />
                            </Col>
                            { (dueCount.dueNow > 0) &&
                            <Col style={{marginLeft: '5px'}}>
                                <b>{`${dueCount.dueNow} tasks due today.`}</b>
                            </Col>
                            }
                        </Row>
                    </div>
                    <div>
                        <Row>
                            <Col>
                                <Radio value={TaskDueType.Soon}>Due Soon</Radio>
                            </Col>
                            <Col>
                                <TaskDueIndicator alert={TaskDueType.Soon} />
                            </Col>
                            { (dueCount.dueSoon > 0) &&
                                <Col style={{marginLeft: '5px'}}>
                                    <b>{`${dueCount.dueSoon} tasks due soon.`}</b>
                                </Col>
                            }
                        </Row>
                    </div>
                </Radio.Group>
                }
            </Card>
        }
        <Table dataSource={filteredTasks} columns={columns} pagination={tablePagination} onChange={onTableChange}/>
    </div>
}